// ==========================================================
// AuthContext.js
// ==========================================================

import React, { createContext, useState, useContext, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { login, logout, validateSession, clearTokenLocally, addBearerInterceptor, apiClient } from "../context/auth";
import { toast } from "react-toastify";
import axios from "axios";

// ==========================================================
// ModuleRouteMap (Sidebar / Routing Config)
// ==========================================================
const ModuleRouteMap = {
  CGOV_MOD: {
    name: "Cookie Governance",
    icon: "🍪",
    features: {
      // SCAN_MGT: {
      //   name: "Scan Management",
      //   path: "/cookie-governance/scan-management",
      //   icon: "🔍",
      // },
      SITE_MGT: {
        name: "Sites",
        path: "/cookie-governance/site-management",
        icon: "🌐",
      },
      AUDT_MGT: {
        name: "Audit Kickoff",
        path: "/cookie-governance/audit-kickoff",
        icon: "🚀",
      },
      RPRT_MGT: {
        name: "Reports",
        path: "/cookie-governance/report-management",
        icon: "📈",
      },
      STTG_MGT: {
        name: "Settings",
        path: "/cookie-governance/settings",
        icon: "⚙️",
      },
      PUBL_MGT: {
        name: "Publish Sites",
        path: "/cookie-governance/publish-sites",
        icon: "📤",
      },
    },
    default_path: "/cookie-governance/site-management",
  },
  SYST_MOD: {
    name: "System",
    icon: "⚙️",
    features: {
      CLNT_MGT: {
        name: "Clients",
        path: "/external-system/client-management",
        icon: "👥",
      },
      CRED_MGT: {
        name: "Client Credentials",
        path: "/external-system/credential-management",
        icon: "🔑",
      },
      SYST_MGT: {
        name: "Systems",
        path: "/external-system/system-management",
        icon: "🔧",
      },
      SYST_MGT_TYPE: {
        featureCode: "SYST_MGT",
        name: "System Types",
        path: "/external-system/system-types-management",
        icon: "📋",
      },
    },
    default_path: "/external-system/client-management",
  },
  ADMN_MOD: {
    name: "Administration",
    icon: "🛡️",
    alwaysVisible: true,
    features: {
      PRIV_MGT: {
        name: "Privileges",
        path: "/administration/privilege-management",
        icon: "🔐",
      },
      MODL_MGT: {
        name: "Modules",
        path: "/administration/module-management",
        icon: "📦",
      },
      FEAT_MGT: {
        name: "Features",
        path: "/administration/feature-management",
        icon: "🧩",
      },
      FMAP_MGT: {
        name: "Feature Privileges",
        path: "/administration/feature-privilege-mapping",
        icon: "🗺️",
      },
      ORGN_MGT: {
        name: "Organizations",
        path: "/administration/organization-management",
        icon: "🏢",
      },
      ROLE_MGT: {
        name: "Roles",
        path: "/admin/role-management",
        icon: "👥",
      },
      USER_MGT: {
        name: "Users",
        path: "/admin/user-management",
        icon: "👤",
      }
    },
    default_path: "/admin/user-management",
  },
  REMY_MOD: {
    name: "Remy",
    icon: "🤖",
    alwaysVisible: true,
    externalUrl: "https://remy.solvation.llc/",
    features: {},
    default_path: null,
  },
};

// ==========================================================
// Utility: Check if a path is accessible to a user
// ==========================================================
export const isPathAuthorized = (path, user) => {
  const moduleFeatures = user?.modules?.module_features || {};
  for (const [moduleCode, moduleConfig] of Object.entries(ModuleRouteMap)) {
    for (const [featureKey, featureConfig] of Object.entries(moduleConfig.features || {})) {
      if (featureConfig.path && path.startsWith(featureConfig.path)) {
        const featureCode = featureConfig.featureCode || featureKey;
        return (moduleFeatures[moduleCode] || []).includes(featureCode);
      }
    }
  }
  return false;
};

// ==========================================================
// Utility: Normalize privilege codes
// ==========================================================
const normalizePrivilegeMap = (raw) => {
  if (!raw || typeof raw !== "object") return {};

  const out = {};

  for (const [code, orgList] of Object.entries(raw)) {
    if (!Array.isArray(orgList)) {
      out[String(code).trim()] = [];
      continue;
    }

    out[String(code).trim()] = orgList
      .map((id) => {
        if (!id) return null;
        if (typeof id === "object") {
          return (
            id?.id ||
            id?._id ||
            id?.organization_id ||
            id?.organizationId ||
            null
          );
        }
        return String(id);
      })
      .filter(Boolean);
  }

  return out;
};

// ==========================================================
// Org utilities
// ==========================================================
const flattenOrgHierarchy = (hierarchy) => {
  let ids = [];
  if (!hierarchy) return ids;

  for (const org of hierarchy) {
    ids.push(org.organization_id);
    if (org.children?.length) {
      ids = ids.concat(flattenOrgHierarchy(org.children));
    }
  }
  return ids;
};

const buildOrgDescendantsMap = (hierarchy) => {
  const map = {};

  const dfs = (node) => {
    let all = [];
    if (node.children?.length) {
      for (const child of node.children) {
        all.push(child.organization_id);
        const childDesc = dfs(child);
        if (childDesc?.length) all = all.concat(childDesc);
      }
    }
    map[node.organization_id] = all;
    return all;
  };

  if (!hierarchy) return map;
  for (const root of hierarchy) dfs(root);

  return map;
};

const AuthContext = createContext(null);

// Shared across tabs so activity in any tab resets every tab's idle timer.
const IDLE_ACTIVITY_KEY = "idle_last_activity";
const IDLE_ACTIVITY_BROADCAST_MS = 5000;

// ==========================================================
// Provider
// ==========================================================
export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();

  // Main state
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [tenantList, setTenantList] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(null);

  // Multi-org selection: array of org IDs, null = not yet initialized
  const [selectedOrgIds, setSelectedOrgIdsState] = useState(() => {
    try {
      const saved = localStorage.getItem("active_org_ids");
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [orgContextVersion, setOrgContextVersion] = useState(0);
  const [orgSwitching, setOrgSwitching] = useState(false);

  // Backward compat: single selected org = first in array
  const selectedOrgId = selectedOrgIds?.[0] ?? null;

  // Log last_path on every route change (exclude "/" and "/login")
  useEffect(() => {
    const handlePath = () => {
      const path = window.location.pathname;
      const AUTH_PATHS = ["/", "/login", "/forgot-password"];
      if (!AUTH_PATHS.includes(path) && !path.startsWith("/changepassword") && path.length > 1) {
        localStorage.setItem("last_path", path);
      }
    };
    window.addEventListener("popstate", handlePath);
    handlePath();
    return () => window.removeEventListener("popstate", handlePath);
  }, []);

  // ==========================================================
  // Privilege Helpers
  // ==========================================================
  const getAllowedOrganizationsForPrivilege = useCallback((privCode) => {
    if (!privCode || !user?.authorization_codes) return [];
    const topOrgs = user.authorization_codes[privCode] || [];
    const descendantsMap = user.org_descendants_map || {};

    const out = new Set();
    for (const orgId of topOrgs) {
      out.add(orgId);
      const childList = descendantsMap[orgId];
      if (childList?.length) childList.forEach((x) => out.add(x));
    }
    return Array.from(out);
  }, [user]);

  const getAllowedOrgsForPrefix = useCallback((prefix) => {
    if (!prefix) return [];
    const SFX = { create: "_CRT", edit: "_EDT", delete: "_DEL", view: "_VIW" };

    const collect = (suffix) =>
      getAllowedOrganizationsForPrivilege(prefix + suffix);

    return Array.from(
      new Set([
        ...collect(SFX.create),
        ...collect(SFX.edit),
        ...collect(SFX.delete),
        ...collect(SFX.view),
      ]),
    );
  }, [getAllowedOrganizationsForPrivilege]);

  // ==========================================================
  // Tenant + Org Context
  // ==========================================================
  const setTenantContext = useCallback((userData) => {
    if (userData.tenants_map?.length) {
      setTenantList(userData.tenants_map);

      const saved = localStorage.getItem("selectedTenantId");
      const active = saved &&
        userData.tenants_map.find((t) => String(t.id) === String(saved));

      const fallback = userData.tenants_map.find(
        (t) => String(t.name) === String(userData.tenant_name)
      );

      setSelectedTenantId(
        active?.id || fallback?.id || userData.tenant_id || userData.tenants_map[0].id
      );
    }

    if (userData.org_hierarchy?.length) {
      const allOrgs = flattenOrgHierarchy(userData.org_hierarchy);

      // authorization_codes values hold the org IDs where the user has direct role
      // assignments. Use only these as the default selection so the frontend
      // selectedOrgIds stays in sync with session["active_org_ids"] on the backend
      // (which also only accepts directly-assigned org IDs).
      const assignedOrgIds = [
        ...new Set(
          Object.values(userData.authorization_codes || {})
            .flat()
            .filter(Boolean)
        ),
      ].filter((id) => allOrgs.includes(id));
      const defaultOrgIds = assignedOrgIds.length ? assignedOrgIds : allOrgs;

      try {
        const saved = localStorage.getItem("active_org_ids");
        const savedIds = saved ? JSON.parse(saved) : null;
        // Restore only IDs that are still directly-assigned (valid in backend permissions)
        const validIds = savedIds?.filter((id) => assignedOrgIds.includes(id));
        setSelectedOrgIdsState(validIds?.length ? validIds : defaultOrgIds);
      } catch {
        setSelectedOrgIdsState(defaultOrgIds);
      }

      if (!userData.org_descendants_map) {
        userData.org_descendants_map = buildOrgDescendantsMap(userData.org_hierarchy);
      }
    }
  }, []);

  // ==========================================================
  // Login
  // ==========================================================
  const handleLogin = useCallback(async (username, password) => {
    try {
      const response = await login(username, password);
      const userData = response.user;

      if (userData.org_hierarchy?.length && !userData.org_descendants_map) {
        userData.org_descendants_map = buildOrgDescendantsMap(userData.org_hierarchy);
      }

      userData.authorization_codes = normalizePrivilegeMap(
        userData.authorization_codes
      );
      userData.authorization_codes_flat = Object.keys(
        userData.authorization_codes
      );

      setUser(userData);
      setIsAuthenticated(true);
      localStorage.setItem("user", JSON.stringify(userData));

      setTenantContext(userData);

      const last = localStorage.getItem("last_path");
      const AUTH_PATHS = ["/", "/login", "/forgot-password"];
      const validLast = last && !AUTH_PATHS.includes(last) && !last.startsWith("/changepassword");
      const authorizedLast = validLast && isPathAuthorized(last, userData);
      navigate(authorizedLast ? last : "/welcome");

      return true;
    } catch (err) {
      const msg =
        err.response?.data?.errorMessage ||
        err.response?.data?.error_message ||
        err.response?.data?.message ||
        err.message ||
        "Login failed";
      throw new Error(msg);
    }
  }, [navigate, setTenantContext]);

  // ==========================================================
  // Logout
  // ==========================================================
  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch {}

    setUser(null);
    setIsAuthenticated(false);

    setTenantList([]);
    setSelectedTenantId(null);
    setSelectedOrgIdsState(null);

    localStorage.removeItem("user");
    localStorage.removeItem("selectedTenantId");
    localStorage.removeItem("active_org_ids");
    clearTokenLocally();

    navigate("/login");
  }, [navigate]);

  const toggleSidebar = useCallback(() => setIsSidebarOpen((p) => !p), []);

  const switchTenant = useCallback((id) => {
    if (!id || id === selectedTenantId) return;
    setSelectedTenantId(id);
    localStorage.setItem("selectedTenantId", id);
  }, [selectedTenantId]);

  // Merge partial user data into state and localStorage (e.g. after profile update)
  const updateUserLocally = useCallback((partialData) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...partialData };
      localStorage.setItem("user", JSON.stringify(merged));
      return merged;
    });
  }, []);

  // Re-fetch org_hierarchy from the server and patch local user state.
  // Call after creating or modifying organizations so the header dropdown
  // reflects changes without a logout/login cycle.
  const refreshOrgHierarchy = useCallback(async () => {
    // Snapshot current org IDs before the network call so we can diff afterward.
    const collectIds = (nodes) => {
      const ids = [];
      const walk = (list) => {
        for (const n of (list || [])) {
          if (n.organization_id) ids.push(n.organization_id);
          if (n.children?.length) walk(n.children);
        }
      };
      walk(nodes);
      return ids;
    };
    const prevOrgIdSet = new Set(collectIds(user?.org_hierarchy));

    try {
      const res = await apiClient.get(`/auth/session/org-hierarchy`);
      const data = res?.data || {};
      const newHierarchy = data.org_hierarchy || [];

      const updates = { org_hierarchy: newHierarchy };
      if (newHierarchy.length) {
        updates.org_descendants_map = buildOrgDescendantsMap(newHierarchy);
      }
      if (data.authorization_codes) {
        updates.authorization_codes = normalizePrivilegeMap(data.authorization_codes);
        updates.authorization_codes_flat = Object.keys(updates.authorization_codes);
      }
      updateUserLocally(updates);

      // Detect org IDs that appear in the new hierarchy but not the old one.
      // Comparing hierarchies (not active_org_ids) is correct because
      // frame_user_session resets session["active_org_ids"] to ALL org IDs,
      // so active_org_ids cannot be used to identify truly new orgs.
      const trulyNewIds = collectIds(newHierarchy).filter((id) => !prevOrgIdSet.has(id));
      if (trulyNewIds.length) {
        // Use functional updater to read actual current state — localStorage may be
        // null if the user never explicitly switched orgs, causing only newIds to be kept.
        setSelectedOrgIdsState((prev) => {
          const updated = [...new Set([...(prev || []), ...trulyNewIds])];
          localStorage.setItem("active_org_ids", JSON.stringify(updated));
          return updated;
        });
        // sync useEffect will POST /session/orgs and then increment orgContextVersion
        return;
      }
      setOrgContextVersion((v) => v + 1);
    } catch (e) {
      console.error("refreshOrgHierarchy failed", e);
    }
  }, [updateUserLocally, setOrgContextVersion, user?.org_hierarchy]);

  // Multi-org setter (backward compat — single selection only now)
  const setActiveOrgIds = useCallback((ids) => {
    setOrgSwitching(true);
    setSelectedOrgIdsState(ids);
    localStorage.setItem("active_org_ids", JSON.stringify(ids));
    // orgContextVersion increment handled by useEffect after API call
  }, []);

  // Single-org setter — calls /session/orgs first so pages reload with correct backend data
  const setActiveOrg = useCallback(async (orgId) => {
    if (!orgId) return;
    setOrgSwitching(true);
    const newIds = [orgId];
    const prevStored = localStorage.getItem("active_org_ids");
    localStorage.setItem("active_org_ids", JSON.stringify(newIds));
    skipNextSyncRef.current = true;
    try {
      const res = await apiClient.post(
        "/auth/session/orgs",
        { org_ids: newIds }
      );
      const { authorization_codes, modules } = res.data ?? {};
      if (authorization_codes && Object.keys(authorization_codes).length > 0) {
        const normalized = normalizePrivilegeMap(authorization_codes);
        setUser((prev) => {
          if (!prev) return prev;
          const updated = {
            ...prev,
            authorization_codes:      normalized,
            authorization_codes_flat: Object.keys(normalized),
            modules: modules?.module?.length > 0 ? modules : prev.modules,
          };
          localStorage.setItem("user", JSON.stringify(updated));
          return updated;
        });
      }
      setSelectedOrgIdsState(newIds);
      setOrgContextVersion((v) => v + 1);
      setOrgSwitching(false);
    } catch {
      // POST failed — revert optimistic localStorage so UI stays consistent with backend session
      if (prevStored != null) {
        localStorage.setItem("active_org_ids", prevStored);
      } else {
        localStorage.removeItem("active_org_ids");
      }
      skipNextSyncRef.current = false;
      setOrgSwitching(false);
    }
  }, []);

  // ==========================================================
  // Flat Org List (backward compat — used by pages for name lookups)
  // ==========================================================
  const flatOrgList = useMemo(() => {
    const out = [];

    const dfs = (nodes, level = 0) => {
      if (!nodes) return;
      for (const n of nodes) {
        out.push({
          id: n.organization_id,
          name: n.organization_name || n.name,
          indent: "—".repeat(level),
        });
        if (n.children?.length) dfs(n.children, level + 1);
      }
    };

    if (user?.org_hierarchy?.length) dfs(user.org_hierarchy, 0);
    return out;
  }, [user]);

  // ==========================================================
  // Session Restore with Backend Validation
  // ==========================================================
  useEffect(() => {
    const restoreSession = async () => {
      const stored = localStorage.getItem("user");
      if (!stored) {
        setAuthLoading(false);
        return;
      }

      // Unblock render immediately using cached data — user sees the app at once.
      const userData = JSON.parse(stored);
      if (userData.org_hierarchy?.length && !userData.org_descendants_map) {
        userData.org_descendants_map = buildOrgDescendantsMap(userData.org_hierarchy);
      }
      userData.authorization_codes = normalizePrivilegeMap(userData.authorization_codes);
      userData.authorization_codes_flat = Object.keys(userData.authorization_codes);
      setUser(userData);
      setIsAuthenticated(true);
      setTenantContext(userData);
      setAuthLoading(false);

      // Validate with backend in background; redirect on failure.
      const isValid = await validateSession();

      // Guard: a concurrent handleLogin may have updated localStorage mid-flight.
      const currentStored = localStorage.getItem("user");
      if (currentStored !== stored) return;

      if (!isValid) {
        localStorage.removeItem("user");
        localStorage.removeItem("selectedTenantId");
        localStorage.removeItem("active_org_ids");
        setUser(null);
        setIsAuthenticated(false);
      }
    };

    restoreSession();
  }, [setTenantContext]);

  // ==========================================================
  // Idle Timeout
  // ==========================================================
  const _idleTimeoutMin  = Number(window.__ENV__?.IDLE_TIMEOUT_MINUTES  ?? 30);
  const _idleWarningMin  = Number(window.__ENV__?.IDLE_WARNING_MINUTES  ?? 25);
  const IDLE_TIMEOUT_MS  = _idleTimeoutMin  * 60 * 1000;
  const IDLE_WARNING_MS  = _idleWarningMin  * 60 * 1000;

  const handleLogoutRef  = useRef(null);
  handleLogoutRef.current = handleLogout;

  const idleTimerRef    = useRef(null);
  const warningTimerRef = useRef(null);

  const resetIdleTimer = useCallback(() => {
    if (IDLE_TIMEOUT_MS <= 0) return;

    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    toast.dismiss("idle-warning");

    const minutesLeft = _idleTimeoutMin - _idleWarningMin;

    if (IDLE_WARNING_MS > 0 && IDLE_WARNING_MS < IDLE_TIMEOUT_MS) {
      warningTimerRef.current = setTimeout(() => {
        toast.warning(
          `You'll be logged out in ${minutesLeft} minute${minutesLeft !== 1 ? "s" : ""} due to inactivity. Move the mouse or press any key to stay logged in.`,
          { toastId: "idle-warning", autoClose: minutesLeft * 60 * 1000 }
        );
      }, IDLE_WARNING_MS);
    }

    idleTimerRef.current = setTimeout(() => {
      toast.dismiss("idle-warning");
      toast.info("Logged out due to inactivity.", { toastId: "idle-logout", autoClose: 4000 });
      handleLogoutRef.current?.();
    }, IDLE_TIMEOUT_MS);
  }, [IDLE_TIMEOUT_MS, IDLE_WARNING_MS, _idleTimeoutMin, _idleWarningMin]);

  useEffect(() => {
    if (!isAuthenticated || IDLE_TIMEOUT_MS <= 0) {
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      toast.dismiss("idle-warning");
      return;
    }

    const EVENTS = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    // Broadcast activity to other tabs (they share one session token, so an
    // idle tab logging out would also kick out the tab being used).
    let lastBroadcast = 0;
    const onActivity = () => {
      resetIdleTimer();
      const now = Date.now();
      if (now - lastBroadcast > IDLE_ACTIVITY_BROADCAST_MS) {
        lastBroadcast = now;
        localStorage.setItem(IDLE_ACTIVITY_KEY, String(now));
      }
    };
    // The storage event only fires in the *other* tabs.
    const onOtherTabActivity = (e) => {
      if (e.key === IDLE_ACTIVITY_KEY) resetIdleTimer();
    };

    EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    window.addEventListener("storage", onOtherTabActivity);
    resetIdleTimer();

    return () => {
      EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.removeEventListener("storage", onOtherTabActivity);
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      toast.dismiss("idle-warning");
    };
  }, [isAuthenticated, resetIdleTimer, IDLE_TIMEOUT_MS]);

  // ==========================================================
  // Session Heartbeat
  // ==========================================================
  useEffect(() => {
    if (!isAuthenticated) return;

    const HEARTBEAT_MS = 2 * 60 * 1000;

    const heartbeat = async () => {
      const still_valid = await validateSession();
      if (!still_valid) {
        toast.error("Your session has expired. Please log in again.", {
          toastId: "session-expired",
        });
        handleLogoutRef.current?.();
      }
    };

    const timer = setInterval(heartbeat, HEARTBEAT_MS);
    return () => clearInterval(timer);
  }, [isAuthenticated]);

  // ==========================================================
  // Sync active org selection to backend session
  // Only fires when org content actually changes (not on page-load re-initialization).
  // Initialized from localStorage so setTenantContext restoring the same IDs is a no-op.
  // ==========================================================
  const lastSyncedOrgIdsRef = useRef(
    (() => { try { return localStorage.getItem("active_org_ids") ?? "[]"; } catch { return "[]"; } })()
  );
  const skipNextSyncRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || !selectedOrgIds?.length) return;
    const currentStr = JSON.stringify(selectedOrgIds);
    if (currentStr === lastSyncedOrgIdsRef.current) { setOrgSwitching(false); return; }
    // Skip when setActiveOrg already handled this org change
    if (skipNextSyncRef.current) { skipNextSyncRef.current = false; lastSyncedOrgIdsRef.current = currentStr; return; }
    lastSyncedOrgIdsRef.current = currentStr;
    apiClient.post(
      "/auth/session/orgs",
      { org_ids: selectedOrgIds }
    ).then((res) => {
      const { authorization_codes, modules } = res.data ?? {};
      if (!authorization_codes || !Object.keys(authorization_codes).length) {
        setOrgSwitching(false);
        return;
      }
      const normalized = normalizePrivilegeMap(authorization_codes);
      setUser((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          authorization_codes:      normalized,
          authorization_codes_flat: Object.keys(normalized),
          modules: modules?.module?.length > 0 ? modules : prev.modules,
        };
        localStorage.setItem("user", JSON.stringify(updated));
        return updated;
      });
      setOrgContextVersion((v) => v + 1);
      setOrgSwitching(false);
    }).catch(() => { setOrgSwitching(false); });
  }, [isAuthenticated, selectedOrgIds]);

  // ==========================================================
  // Context Value
  // ==========================================================
  const ctx = useMemo(() => ({
    user,
    isAuthenticated,
    authLoading,
    isSidebarOpen,
    toggleSidebar,
    handleLogin,
    handleLogout,
    ModuleRouteMap,
    selectedTenantId,
    selectedOrgIds,
    selectedOrgId,          // backward compat: first selected org
    orgContextVersion,
    tenantList,
    switchTenant,
    orgSwitching,
    setActiveOrgIds,
    setActiveOrg,
    setSelectedOrgId: setActiveOrg,  // backward compat
    flatOrgList,
    getAllowedOrganizationsForPrivilege,
    getAllowedOrgsForPrefix,
    updateUserLocally,
    refreshOrgHierarchy,
  }), [
    user,
    isAuthenticated,
    authLoading,
    isSidebarOpen,
    toggleSidebar,
    handleLogin,
    handleLogout,
    selectedTenantId,
    selectedOrgIds,
    selectedOrgId,
    orgContextVersion,
    tenantList,
    switchTenant,
    orgSwitching,
    setActiveOrgIds,
    setActiveOrg,
    flatOrgList,
    getAllowedOrganizationsForPrivilege,
    getAllowedOrgsForPrefix,
    updateUserLocally,
    refreshOrgHierarchy,
  ]);

  return <AuthContext.Provider value={ctx}>{children}</AuthContext.Provider>;
};

// ==========================================================
// Hook: useAuth
// ==========================================================
export const useAuth = () => useContext(AuthContext);

// ==========================================================
// Hook: useApiClient
// ==========================================================
export const useApiClient = () => {
  const {
    selectedTenantId,
    handleLogout,
    selectedOrgIds,
  } = useAuth();

  const API_BASE_URL =
    window.__ENV__?.API_BASE_URL ?? "";

  if (!API_BASE_URL) {
    throw new Error("API_BASE_URL is not configured");
  }

  const logoutInProgressRef = useRef(false);

  // Ref so the interceptor always reads the latest org IDs without
  // requiring selectedOrgIds in useMemo deps (which would recreate the client).
  const selectedOrgIdsRef = useRef(selectedOrgIds);
  selectedOrgIdsRef.current = selectedOrgIds;

  const client = useMemo(() => {
    const c = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
      },
    });

    addBearerInterceptor(c);

    c.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          if (!logoutInProgressRef.current) {
            logoutInProgressRef.current = true;
            toast.error("Your session has expired. Please log in again.", {
              toastId: "session-expired",
            });
            setTimeout(() => {
              logoutInProgressRef.current = false;
              handleLogout();
            }, 1500);
          }
          return new Promise(() => {});
        }
        const data = err?.response?.data;
        if (data) {
          const apiMessage = data.message || data.errorMessage || data.error_message || data.error || data.error_code;
          if (apiMessage) {
            err.message = apiMessage;
          }
        }
        return Promise.reject(err);
      }
    );

    c.interceptors.request.use((config) => {
      if (selectedTenantId) {
        if (["get", "delete"].includes(config.method)) {
          config.params = { ...config.params, tenantId: selectedTenantId };
        } else {
          config.data = { ...config.data, tenantId: selectedTenantId };
        }
      }

      return config;
    });

    return c;
  }, [API_BASE_URL, selectedTenantId, handleLogout]);

  return client;
};
